import { useEffect, useState } from "react";
import {
    Box, Button, Chip, Grid, Tab, Tabs, Typography,
    Paper, Stack, Divider, TextField, Pagination,
    Select, MenuItem, FormControl, Collapse, Badge, Tooltip, IconButton
} from "@mui/material";
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RemoveIcon from '@mui/icons-material/Remove';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import moment from "moment";
import Templete from "../../template/Template";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { isPermite } from "../../utilities/isPermite";
import { TarefaPrioridade, TarefaType } from "../../types/TarefaType";
import ModalCriarTarefa from "./ModalCriarTarefa";
import ModalEditarTarefa from "./ModalEditarTarefa";

// ─── Helpers ───────────────────────────────────────────────────────────────────
const PRIORIDADE_ORDEM: Record<TarefaPrioridade, number> = { urgente: 1, prioridade: 2, normal: 3 };

function isVencida(data: any) { return moment(data).isBefore(moment(), 'day'); }
function isHoje(data: any) { return moment(data).isSame(moment(), 'day'); }
function isAmanha(data: any) { return moment(data).isSame(moment().add(1, 'day'), 'day'); }

function labelData(dateStr: string): string {
    if (isVencida(dateStr)) return `Vencida — ${moment(dateStr).format('DD/MM/YYYY')}`;
    if (isHoje(dateStr)) return `Hoje — ${moment(dateStr).format('DD/MM/YYYY')}`;
    if (isAmanha(dateStr)) return `Amanhã — ${moment(dateStr).format('DD/MM/YYYY')}`;
    return moment(dateStr).format('dddd, DD/MM/YYYY');
}

function corData(dateStr: string): string {
    if (isVencida(dateStr)) return '#c62828';
    if (isHoje(dateStr)) return '#1565c0';
    if (isAmanha(dateStr)) return '#2e7d32';
    return '#555';
}

function bgData(dateStr: string): string {
    if (isVencida(dateStr)) return '#ffebee';
    if (isHoje(dateStr)) return '#e3f2fd';
    if (isAmanha(dateStr)) return '#e8f5e9';
    return '#f5f5f5';
}

const PER_PAGE = 20;

// Ordena tarefas por data DESC e, dentro da mesma data, por prioridade ASC
function sortDesc(tarefas: TarefaType[]): TarefaType[] {
    return [...tarefas].sort((a, b) => {
        const dateDiff = String(b.data_tarefa).slice(0, 10).localeCompare(String(a.data_tarefa).slice(0, 10));
        if (dateDiff !== 0) return dateDiff;
        return PRIORIDADE_ORDEM[a.prioridade_tarefa] - PRIORIDADE_ORDEM[b.prioridade_tarefa];
    });
}

// Agrupa por data (YYYY-MM-DD) — já recebe lista ordenada, mantém a ordem
function groupByDate(tarefas: TarefaType[]): [string, TarefaType[]][] {
    const map = new Map<string, TarefaType[]>();
    tarefas.forEach(t => {
        const key = String(t.data_tarefa).slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(t);
    });
    // Datas em ordem decrescente
    const entries: [string, TarefaType[]][] = Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));
    // Dentro de cada data, ordena por prioridade
    entries.forEach(([, list]) => list.sort((a: TarefaType, b: TarefaType) => PRIORIDADE_ORDEM[a.prioridade_tarefa] - PRIORIDADE_ORDEM[b.prioridade_tarefa]));
    return entries;
}

// ─── PriorityChip ───────────────────────────────────────────────────────────────
type PriorityChipProps = { prioridade: TarefaPrioridade };
function PriorityChip({ prioridade }: PriorityChipProps) {
    if (prioridade === 'urgente')
        return <Chip size="small" icon={<ErrorOutlineIcon />} label="Urgente"
            sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 'bold', '& .MuiChip-icon': { color: '#c62828' } }} />;
    if (prioridade === 'prioridade')
        return <Chip size="small" icon={<PriorityHighIcon />} label="Prioridade"
            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold', '& .MuiChip-icon': { color: '#e65100' } }} />;
    return <Chip size="small" icon={<RemoveIcon />} label="Normal"
        sx={{ bgcolor: '#f5f5f5', color: '#757575', '& .MuiChip-icon': { color: '#9e9e9e' } }} />;
}

// ─── StatusChip ────────────────────────────────────────────────────────────────
type StatusChipProps = { tarefa: TarefaType };
function StatusChip({ tarefa }: StatusChipProps) {
    if (tarefa.status_tarefa === 'concluida')
        return <Chip size="small" icon={<CheckCircleIcon />} label="Concluída" color="success" />;
    if (tarefa.status_tarefa === 'reagendamento_solicitado')
        return <Chip size="small" icon={<HourglassTopIcon />} label="Aguard. Reagendamento" color="warning" />;
    if (tarefa.status_tarefa === 'aguardando_edicao')
        return <Chip size="small" icon={<PendingActionsIcon />} label="Aguard. Edição"
            sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 'bold', '& .MuiChip-icon': { color: '#4527a0' } }} />;
    if (isVencida(tarefa.data_tarefa))
        return <Chip size="small" label="Vencida" color="error" />;
    if (isHoje(tarefa.data_tarefa))
        return <Chip size="small" label="Hoje" color="primary" />;
    return <Chip size="small" label={moment(tarefa.data_tarefa).format('DD/MM/YYYY')} color="default" />;
}

// ─── DateGroupHeader ───────────────────────────────────────────────────────────
function DateGroupHeader({ dateStr }: { dateStr: string }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, mt: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 15, color: corData(dateStr) }} />
            <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                    color: corData(dateStr),
                    bgcolor: bgData(dateStr),
                    px: 1.2, py: 0.3,
                    borderRadius: 1,
                    textTransform: 'capitalize',
                    letterSpacing: 0.4,
                }}
            >
                {labelData(dateStr)}
            </Typography>
        </Stack>
    );
}

// ─── Card de Tarefa (Minhas Tarefas) ───────────────────────────────────────────
type MinhasTarefasCardProps = {
    tarefa: TarefaType;
    onConcluir: (id: number) => void;
    onRemarcar: (id: number, motivo: string) => void;
};
function MinhasTarefasCard({ tarefa, onConcluir, onRemarcar }: MinhasTarefasCardProps) {
    const [showRemarcar, setShowRemarcar] = useState(false);
    const [motivo, setMotivo] = useState('');

    const concluida = tarefa.status_tarefa === 'concluida';
    const aguardandoEdicao = tarefa.status_tarefa === 'aguardando_edicao';
    const urgente = tarefa.prioridade_tarefa === 'urgente';
    const prio = tarefa.prioridade_tarefa === 'prioridade';

    function handleConfirmarRemarcar() {
        if (!motivo.trim()) return AlertInfo({ icon: 'info', title: 'Informe o motivo da remarcação' });
        onRemarcar(tarefa.id_tarefa, motivo);
        setShowRemarcar(false);
        setMotivo('');
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2, borderRadius: 2, opacity: concluida ? 0.7 : 1,
                borderLeft: urgente ? '4px solid #c62828' : prio ? '4px solid #e65100' : undefined,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.3}>
                        {!concluida && <PriorityChip prioridade={tarefa.prioridade_tarefa} />}
                        <Typography variant="subtitle2" fontWeight="bold">{tarefa.titulo_tarefa}</Typography>
                    </Stack>
                    {tarefa.descricao_tarefa && (
                        <Typography variant="body2" color="text.secondary" mt={0.3}>{tarefa.descricao_tarefa}</Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                        Atribuída por: <strong>{tarefa.nome_criador}</strong>
                        {tarefa.data_conclusao && ` · Concluída em ${moment(tarefa.data_conclusao).format('DD/MM/YYYY HH:mm')}`}
                    </Typography>
                    {aguardandoEdicao && tarefa.observacao_reagendamento && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#ede7f6', borderRadius: 1, border: '1px solid #ce93d8' }}>
                            <Typography variant="caption" color="#4527a0">
                                ✏️ Remarcação solicitada — aguardando edição do responsável
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Motivo: {tarefa.observacao_reagendamento}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box ml={1}><StatusChip tarefa={tarefa} /></Box>
            </Stack>

            {/* Botões de ação */}
            {!concluida && !aguardandoEdicao && !showRemarcar && (
                <Stack direction="row" spacing={1} mt={1.5}>
                    <Button size="small" variant="contained" color="success" disableElevation
                        startIcon={<CheckCircleOutlineIcon />} onClick={() => onConcluir(tarefa.id_tarefa)}>
                        Concluir
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<EventRepeatIcon />}
                        sx={{ color: '#4527a0', borderColor: '#7e57c2', '&:hover': { borderColor: '#4527a0', bgcolor: '#f3e5f5' } }}
                        onClick={() => setShowRemarcar(true)}>
                        Remarcar
                    </Button>
                </Stack>
            )}

            {/* Form inline de remarcação */}
            {showRemarcar && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                    <Typography variant="caption" fontWeight="bold" color="#4527a0" display="block" mb={1}>
                        Motivo da remarcação
                    </Typography>
                    <TextField
                        fullWidth size="small" multiline rows={2}
                        placeholder="Descreva o motivo..."
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                        sx={{ mb: 1, bgcolor: '#fff', borderRadius: 1 }}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" disableElevation
                            sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}
                            onClick={handleConfirmarRemarcar}>
                            Confirmar
                        </Button>
                        <Button size="small" variant="outlined" color="inherit"
                            onClick={() => { setShowRemarcar(false); setMotivo(''); }}>
                            Cancelar
                        </Button>
                    </Stack>
                </Box>
            )}
        </Paper>
    );
}

// ─── Card de Tarefa (Tarefas Criadas) ──────────────────────────────────────────
type CriadasCardProps = {
    tarefa: TarefaType;
    onAprovar: (tarefa: TarefaType) => void;
    onEditar: (tarefa: TarefaType) => void;
    onDeletar: (id: number) => void;
    podeEditar: boolean;
    podeDeletar: boolean;
};
function CriadasCard({ tarefa, onAprovar, onEditar, onDeletar, podeEditar, podeDeletar }: CriadasCardProps) {
    const concluida = tarefa.status_tarefa === 'concluida';
    const aguardando = tarefa.status_tarefa === 'reagendamento_solicitado';
    const aguardandoEdicao = tarefa.status_tarefa === 'aguardando_edicao';
    const urgente = tarefa.prioridade_tarefa === 'urgente';
    const prio = tarefa.prioridade_tarefa === 'prioridade';

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2, borderRadius: 2, opacity: concluida ? 0.7 : 1,
                borderLeft: aguardandoEdicao ? '4px solid #7e57c2'
                    : urgente ? '4px solid #c62828'
                    : prio ? '4px solid #e65100'
                    : undefined,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.3}>
                        {!concluida && <PriorityChip prioridade={tarefa.prioridade_tarefa} />}
                        <Typography variant="subtitle2" fontWeight="bold">{tarefa.titulo_tarefa}</Typography>
                    </Stack>
                    {tarefa.descricao_tarefa && (
                        <Typography variant="body2" color="text.secondary" mt={0.3}>{tarefa.descricao_tarefa}</Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                        Responsável: <strong>{tarefa.nome_responsavel}</strong>
                    </Typography>

                    {/* Banner: aguardando edição */}
                    {aguardandoEdicao && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#ede7f6', border: '1px solid #ce93d8', borderRadius: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="#4527a0" display="block">
                                ✏️ {tarefa.nome_responsavel} solicitou remarcação
                            </Typography>
                            {tarefa.observacao_reagendamento && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Motivo: {tarefa.observacao_reagendamento}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Banner: reagendamento solicitado */}
                    {aguardando && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block">
                                ⏳ {tarefa.nome_responsavel} solicitou reagendamento
                            </Typography>
                            {tarefa.observacao_reagendamento && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Motivo: {tarefa.observacao_reagendamento}
                                </Typography>
                            )}
                            {tarefa.nova_data_sugerida && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Data sugerida: {moment(tarefa.nova_data_sugerida).format('DD/MM/YYYY')}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="flex-start" ml={1}>
                    <StatusChip tarefa={tarefa} />
                    {podeEditar && !concluida && (
                        <Button size="small" variant="text" color="primary" sx={{ minWidth: 0, p: 0.5 }}
                            onClick={() => onEditar(tarefa)}>
                            <EditIcon fontSize="small" />
                        </Button>
                    )}
                    {podeDeletar && (
                        <Button size="small" variant="text" color="error" sx={{ minWidth: 0, p: 0.5 }}
                            onClick={() => onDeletar(tarefa.id_tarefa)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </Button>
                    )}
                </Stack>
            </Stack>
            {aguardando && (
                <Stack direction="row" spacing={1} mt={1.5}>
                    <Button size="small" variant="contained" color="warning" disableElevation
                        startIcon={<ThumbUpAltIcon />} onClick={() => onAprovar(tarefa)}>
                        Aprovar e Reagendar
                    </Button>
                </Stack>
            )}
            {aguardandoEdicao && podeEditar && (
                <Stack direction="row" spacing={1} mt={1.5}>
                    <Button size="small" variant="contained" disableElevation
                        startIcon={<EditIcon />}
                        sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}
                        onClick={() => onEditar(tarefa)}>
                        Editar Tarefa
                    </Button>
                </Stack>
            )}
        </Paper>
    );
}

// ─── Modal de Reagendamento ─────────────────────────────────────────────────────
type ModalReagendarProps = { tarefa: TarefaType | null; onClose: () => void; onSaved: () => void; isCriador?: boolean; };
function ModalReagendar({ tarefa, onClose, onSaved, isCriador = false }: ModalReagendarProps) {
    const [motivo, setMotivo] = useState('');
    const [novaData, setNovaData] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (tarefa) {
            setMotivo('');
            setNovaData(tarefa.nova_data_sugerida ? String(tarefa.nova_data_sugerida).slice(0, 10) : '');
        }
    }, [tarefa]);

    if (!tarefa) return <></>;

    async function handleSolicitar() {
        if (!motivo.trim()) return AlertInfo({ icon: 'info', title: 'Informe o motivo do reagendamento' });
        setLoading(true);
        try {
            const body = { status_tarefa: 'reagendamento_solicitado', observacao_reagendamento: motivo, nova_data_sugerida: novaData || null };
            const res = await api.post(`/updatetarefa/${tarefa!.id_tarefa}`, body);
            if (res.data.success) { AlertInfo({ icon: 'success', title: 'Solicitação enviada!' }); onSaved(); onClose(); }
            else AlertInfo({ icon: 'error', title: res.data.message || 'Falha' });
        } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
        setLoading(false);
    }

    async function handleAprovar() {
        if (!novaData) return AlertInfo({ icon: 'info', title: 'Informe a nova data' });
        setLoading(true);
        try {
            const res = await api.post(`/aprovarreagendamento/${tarefa!.id_tarefa}`, { nova_data_tarefa: novaData });
            if (res.data.success) { AlertInfo({ icon: 'success', title: 'Reagendamento aprovado!' }); onSaved(); onClose(); }
            else AlertInfo({ icon: 'error', title: res.data.message || 'Falha' });
        } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
        setLoading(false);
    }

    return (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Paper sx={{ width: 420, p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    {isCriador ? 'Aprovar Reagendamento' : 'Solicitar Reagendamento'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" mb={2}>{tarefa.titulo_tarefa}</Typography>
                {!isCriador && (
                    <TextField label="Motivo *" fullWidth size="small" multiline rows={3} value={motivo}
                        onChange={e => setMotivo(e.target.value)} sx={{ mb: 2 }} />
                )}
                <TextField label={isCriador ? 'Nova Data *' : 'Sugerir nova data (opcional)'}
                    fullWidth size="small" type="date" InputLabelProps={{ shrink: true }}
                    value={novaData} onChange={e => setNovaData(e.target.value)} sx={{ mb: 3 }} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" color="inherit" onClick={onClose}>Cancelar</Button>
                    <Button variant="contained" color={isCriador ? 'warning' : 'primary'} disableElevation
                        disabled={loading} onClick={isCriador ? handleAprovar : handleSolicitar}>
                        {isCriador ? 'Confirmar Nova Data' : 'Enviar Solicitação'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}

// ─── Página Principal ───────────────────────────────────────────────────────────
export default function Tarefas() {
    const [openLoad, setOpenLoad] = useState(false);
    const [tab, setTab] = useState(0);
    const [minhasTarefas, setMinhasTarefas] = useState<TarefaType[]>([]);
    const [tarefasCriadas, setTarefasCriadas] = useState<TarefaType[]>([]);
    const [openCriar, setOpenCriar] = useState(false);
    const [aprovando, setAprovando] = useState<TarefaType | null>(null);
    const [editando, setEditando] = useState<TarefaType | null>(null);
    const [openEditar, setOpenEditar] = useState(false);

    // Paginação
    const [pagMinhas, setPagMinhas] = useState(1);
    const [pagConclMinhas, setPagConclMinhas] = useState(1);
    const [pagCriadas, setPagCriadas] = useState(1);
    const [pagConclCriadas, setPagConclCriadas] = useState(1);

    // Filtros — Tarefas Criadas
    const [showFiltros, setShowFiltros] = useState(false);
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [filtroPrioridades, setFiltroPrioridades] = useState<TarefaPrioridade[]>([]);
    const [filtroStatuses, setFiltroStatuses] = useState<string[]>([]);
    const [filtroUsuario, setFiltroUsuario] = useState('');

    const podeCriar = isPermite("91");
    const podeEditar = isPermite("92");
    const podeDeletar = isPermite("93");

    async function loadMinhas() {
        try {
            const res = await api.get(`/minhastarefas`);
            if (res.data && !res.data.error) setMinhasTarefas(res.data);
        } catch { AlertInfo({ icon: 'error', title: 'Falha ao carregar tarefas' }); }
    }

    async function loadCriadas() {
        try {
            const res = await api.get(`/tarefascriadas`);
            if (res.data && !res.data.error) setTarefasCriadas(res.data);
        } catch { AlertInfo({ icon: 'error', title: 'Falha ao carregar tarefas criadas' }); }
    }

    async function loadAll() {
        setOpenLoad(true);
        setPagMinhas(1);
        setPagConclMinhas(1);
        setPagCriadas(1);
        setPagConclCriadas(1);
        await Promise.all([loadMinhas(), podeCriar ? loadCriadas() : Promise.resolve()]);
        setOpenLoad(false);
    }

    async function handleRemarcar(id_tarefa: number, motivo: string) {
        setOpenLoad(true);
        try {
            const r = await api.post(`/updatetarefa/${id_tarefa}`, {
                status_tarefa: 'aguardando_edicao',
                observacao_reagendamento: motivo,
            });
            if (r.data.success) {
                AlertInfo({ icon: 'success', title: 'Remarcação solicitada!' });
                loadAll();
            } else {
                AlertInfo({ icon: 'error', title: r.data.message });
            }
        } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
        setOpenLoad(false);
    }

    function handleEditar(tarefa: TarefaType) {
        setEditando(tarefa);
        setOpenEditar(true);
    }

    async function handleDeletar(id_tarefa: number) {
        AlertConfirm({ title: 'Deseja realmente deletar esta tarefa?' }).then(async res => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const r = await api.delete(`/deletetarefa/${id_tarefa}`);
                    if (r.data.success) {
                        AlertInfo({ icon: 'success', title: 'Tarefa deletada!' });
                        loadAll();
                    } else {
                        AlertInfo({ icon: 'error', title: r.data.message });
                    }
                } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
                setOpenLoad(false);
            }
        });
    }

    async function handleConcluir(id_tarefa: number) {
        AlertConfirm({ title: 'Confirmar conclusão da tarefa?' }).then(async res => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const r = await api.post(`/updatetarefa/${id_tarefa}`, { status_tarefa: 'concluida' });
                    if (r.data.success) { AlertInfo({ icon: 'success', title: 'Tarefa concluída!' }); loadAll(); }
                    else AlertInfo({ icon: 'error', title: r.data.message });
                } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
                setOpenLoad(false);
            }
        });
    }

    // ── Helpers de filtro ──────────────────────────────────────────────────────
    function togglePrioridade(p: TarefaPrioridade) {
        setPagCriadas(1); setPagConclCriadas(1);
        setFiltroPrioridades(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
    }

    function toggleStatus(s: string) {
        setPagCriadas(1); setPagConclCriadas(1);
        setFiltroStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    }

    function limparFiltros() {
        setFiltroDataInicio(''); setFiltroDataFim('');
        setFiltroPrioridades([]); setFiltroStatuses([]);
        setFiltroUsuario('');
        setPagCriadas(1); setPagConclCriadas(1);
    }

    function aplicarFiltrosCriadas(lista: TarefaType[]): TarefaType[] {
        return lista.filter(t => {
            const dataStr = String(t.data_tarefa).slice(0, 10);
            if (filtroDataInicio && dataStr < filtroDataInicio) return false;
            if (filtroDataFim && dataStr > filtroDataFim) return false;
            if (filtroPrioridades.length > 0 && !filtroPrioridades.includes(t.prioridade_tarefa)) return false;
            if (filtroStatuses.length > 0) {
                const match = filtroStatuses.some(s => {
                    if (s === 'atrasada') return t.status_tarefa === 'pendente' && isVencida(t.data_tarefa);
                    return t.status_tarefa === s;
                });
                if (!match) return false;
            }
            if (filtroUsuario && String(t.id_usuario_responsavel) !== filtroUsuario) return false;
            return true;
        });
    }

    useEffect(() => { loadAll(); }, []);

    const pendentes = minhasTarefas.filter(t => t.status_tarefa !== 'concluida');
    const concluidas = minhasTarefas.filter(t => t.status_tarefa === 'concluida');

    // Usuários únicos disponíveis para o filtro
    const usuariosDisponiveis = Array.from(
        new Map<number, string>(
            tarefasCriadas
                .filter(t => t.nome_responsavel)
                .map(t => [t.id_usuario_responsavel, t.nome_responsavel!])
        ).entries()
    ).sort(([, a], [, b]) => a.localeCompare(b));

    // Contagem de filtros ativos (para badge)
    const filtrosAtivos = [
        !!(filtroDataInicio || filtroDataFim),
        filtroPrioridades.length > 0,
        filtroStatuses.length > 0,
        !!filtroUsuario,
    ].filter(Boolean).length;

    // Tarefas Criadas com filtros aplicados
    const tarefasCriadasFiltradas = aplicarFiltrosCriadas(tarefasCriadas);
    const criaPendentes = tarefasCriadasFiltradas.filter(t => t.status_tarefa !== 'concluida');
    const criaConcluidas = tarefasCriadasFiltradas.filter(t => t.status_tarefa === 'concluida');

    // ── Paginação: Minhas Tarefas – Pendentes ──────────────────────────────────
    const pendentesSort = sortDesc(pendentes);
    const totalPagMinhas = Math.max(1, Math.ceil(pendentesSort.length / PER_PAGE));
    const pendentesPage = pendentesSort.slice((pagMinhas - 1) * PER_PAGE, pagMinhas * PER_PAGE);
    const pendentesPorData = groupByDate(pendentesPage);

    // ── Paginação: Minhas Tarefas – Concluídas ─────────────────────────────────
    const concluidasSort = sortDesc(concluidas);
    const totalPagConclMinhas = Math.max(1, Math.ceil(concluidasSort.length / PER_PAGE));
    const concluidasPage = concluidasSort.slice((pagConclMinhas - 1) * PER_PAGE, pagConclMinhas * PER_PAGE);

    // ── Paginação: Tarefas Criadas – Em andamento ──────────────────────────────
    const criaSort = sortDesc(criaPendentes);
    const totalPagCriadas = Math.max(1, Math.ceil(criaSort.length / PER_PAGE));
    const criadasPage = criaSort.slice((pagCriadas - 1) * PER_PAGE, pagCriadas * PER_PAGE);
    const criadasPorData = groupByDate(criadasPage);

    // ── Paginação: Tarefas Criadas – Concluídas ────────────────────────────────
    const criaConclSort = sortDesc(criaConcluidas);
    const totalPagConclCriadas = Math.max(1, Math.ceil(criaConclSort.length / PER_PAGE));
    const criaConclPage = criaConclSort.slice((pagConclCriadas - 1) * PER_PAGE, pagConclCriadas * PER_PAGE);

    return (
        <Templete menuActive="tarefas">
            <SimpleBackdrop open={openLoad} />

            <ModalCriarTarefa open={openCriar} onClose={() => setOpenCriar(false)} onSaved={loadAll} />

            <ModalEditarTarefa
                tarefa={editando}
                open={openEditar}
                onClose={() => { setOpenEditar(false); setEditando(null); }}
                onSaved={loadAll}
            />

            {aprovando !== null ? (
                <ModalReagendar tarefa={aprovando} onClose={() => setAprovando(null)} onSaved={loadAll} isCriador={true} />
            ) : <></>}

            <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" fontWeight="bold">Tarefas</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gerencie as tarefas atribuídas a você e ao escritório
                        </Typography>
                    </Box>
                    {podeCriar && (
                        <Button variant="contained" color="primary" disableElevation
                            startIcon={<AddTaskIcon />} onClick={() => setOpenCriar(true)}>
                            Nova Tarefa
                        </Button>
                    )}
                </Stack>
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Tabs value={tab} onChange={(_, v) => { setTab(v); setPagMinhas(1); setPagConclMinhas(1); setPagCriadas(1); setPagConclCriadas(1); }}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
                    <Tab label={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <span>Minhas Tarefas</span>
                            {pendentes.length > 0 && <Chip size="small" label={pendentes.length} color="primary" />}
                        </Stack>
                    } />
                    {podeCriar && (
                        <Tab label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Tarefas Criadas</span>
                                {tarefasCriadas.filter(t => t.status_tarefa === 'reagendamento_solicitado').length > 0 && (
                                    <Chip size="small" label="!" color="warning" />
                                )}
                            </Stack>
                        } />
                    )}
                </Tabs>

                {/* ABA: Minhas Tarefas */}
                {tab === 0 && (
                    <Box sx={{ p: 2 }}>
                        {pendentes.length === 0 && concluidas.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.light' }} />
                                <Typography color="text.secondary" mt={1}>Você não tem tarefas pendentes.</Typography>
                            </Box>
                        )}

                        {/* Pendentes agrupadas por data */}
                        {pendentesPorData.map(([dateStr, tarefas], gi) => (
                            <Box key={dateStr} sx={{ mb: 2 }}>
                                {gi > 0 && <Divider sx={{ mb: 2 }} />}
                                <DateGroupHeader dateStr={dateStr} />
                                <Grid container spacing={1.5}>
                                    {tarefas.map(t => (
                                        <Grid item xs={12} sm={6} key={t.id_tarefa}>
                                            <MinhasTarefasCard tarefa={t} onConcluir={handleConcluir} onRemarcar={handleRemarcar} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}

                        {/* Paginação – Pendentes */}
                        {totalPagMinhas > 1 && (
                            <Stack alignItems="center" mt={2} mb={1}>
                                <Pagination
                                    count={totalPagMinhas}
                                    page={pagMinhas}
                                    onChange={(_, v) => { setPagMinhas(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    color="primary"
                                    size="small"
                                />
                                <Typography variant="caption" color="text.secondary" mt={0.5}>
                                    {pendentes.length} tarefas · página {pagMinhas} de {totalPagMinhas}
                                </Typography>
                            </Stack>
                        )}

                        {/* Concluídas (lista simples ao final) */}
                        {concluidas.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="overline" color="text.secondary" fontWeight="bold">
                                    Concluídas ({concluidas.length})
                                </Typography>
                                <Grid container spacing={1.5} mt={0}>
                                    {concluidasPage.map(t => (
                                        <Grid item xs={12} sm={6} key={t.id_tarefa}>
                                            <MinhasTarefasCard tarefa={t} onConcluir={handleConcluir} onRemarcar={handleRemarcar} />
                                        </Grid>
                                    ))}
                                </Grid>
                                {totalPagConclMinhas > 1 && (
                                    <Stack alignItems="center" mt={2}>
                                        <Pagination
                                            count={totalPagConclMinhas}
                                            page={pagConclMinhas}
                                            onChange={(_, v) => setPagConclMinhas(v)}
                                            color="primary"
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary" mt={0.5}>
                                            {concluidas.length} concluídas · página {pagConclMinhas} de {totalPagConclMinhas}
                                        </Typography>
                                    </Stack>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* ABA: Tarefas Criadas */}
                {tab === 1 && podeCriar && (
                    <Box sx={{ p: 2 }}>

                        {/* ── Barra de filtros ─────────────────────────────── */}
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap">
                            <Badge badgeContent={filtrosAtivos} color="primary" invisible={filtrosAtivos === 0}>
                                <Button
                                    size="small"
                                    variant={showFiltros ? 'contained' : 'outlined'}
                                    startIcon={<FilterListIcon />}
                                    disableElevation
                                    onClick={() => setShowFiltros(v => !v)}
                                >
                                    Filtros
                                </Button>
                            </Badge>
                            {filtrosAtivos > 0 && (
                                <Tooltip title="Limpar todos os filtros">
                                    <Button size="small" variant="text" color="inherit"
                                        startIcon={<CloseIcon fontSize="small" />}
                                        onClick={limparFiltros}>
                                        Limpar ({filtrosAtivos})
                                    </Button>
                                </Tooltip>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {filtrosAtivos > 0
                                    ? `${tarefasCriadasFiltradas.length} de ${tarefasCriadas.length} tarefas`
                                    : `${tarefasCriadas.length} tarefas no total`}
                            </Typography>
                        </Stack>

                        {/* ── Painel expansível de filtros ──────────────────── */}
                        <Collapse in={showFiltros}>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                                <Grid container spacing={2} alignItems="flex-start">

                                    {/* Data início */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                            Data início
                                        </Typography>
                                        <TextField fullWidth size="small" type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={filtroDataInicio}
                                            onChange={e => { setFiltroDataInicio(e.target.value); setPagCriadas(1); setPagConclCriadas(1); }} />
                                    </Grid>

                                    {/* Data fim */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                            Data fim
                                        </Typography>
                                        <TextField fullWidth size="small" type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={filtroDataFim}
                                            onChange={e => { setFiltroDataFim(e.target.value); setPagCriadas(1); setPagConclCriadas(1); }} />
                                    </Grid>

                                    {/* Responsável */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                            <Stack component="span" direction="row" spacing={0.4} alignItems="center">
                                                <PersonIcon sx={{ fontSize: 13 }} /><span>Responsável</span>
                                            </Stack>
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                displayEmpty
                                                value={filtroUsuario}
                                                onChange={e => { setFiltroUsuario(e.target.value); setPagCriadas(1); setPagConclCriadas(1); }}
                                            >
                                                <MenuItem value="">Todos os usuários</MenuItem>
                                                {usuariosDisponiveis.map(([id, nome]) => (
                                                    <MenuItem key={id} value={String(id)}>{nome}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Prioridade */}
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                            Prioridade
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {([
                                                ['urgente',    'Urgente',    '#c62828', '#ffebee'],
                                                ['prioridade', 'Prioridade', '#e65100', '#fff3e0'],
                                                ['normal',     'Normal',     '#616161', '#f5f5f5'],
                                            ] as [TarefaPrioridade, string, string, string][]).map(([val, label, color, bg]) => (
                                                <Chip
                                                    key={val}
                                                    size="small"
                                                    label={label}
                                                    onClick={() => togglePrioridade(val)}
                                                    variant={filtroPrioridades.includes(val) ? 'filled' : 'outlined'}
                                                    sx={filtroPrioridades.includes(val)
                                                        ? { bgcolor: bg, color, fontWeight: 'bold', borderColor: color, cursor: 'pointer' }
                                                        : { borderColor: '#bdbdbd', cursor: 'pointer' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Grid>

                                    {/* Status */}
                                    <Grid item xs={12}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                            Status
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            <Chip size="small" label="Atrasada"
                                                onClick={() => toggleStatus('atrasada')}
                                                variant={filtroStatuses.includes('atrasada') ? 'filled' : 'outlined'}
                                                sx={filtroStatuses.includes('atrasada')
                                                    ? { bgcolor: '#ffebee', color: '#c62828', fontWeight: 'bold', borderColor: '#c62828', cursor: 'pointer' }
                                                    : { borderColor: '#bdbdbd', cursor: 'pointer' }}
                                            />
                                            <Chip size="small" label="Aguard. Edição"
                                                onClick={() => toggleStatus('aguardando_edicao')}
                                                variant={filtroStatuses.includes('aguardando_edicao') ? 'filled' : 'outlined'}
                                                sx={filtroStatuses.includes('aguardando_edicao')
                                                    ? { bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 'bold', borderColor: '#7e57c2', cursor: 'pointer' }
                                                    : { borderColor: '#bdbdbd', cursor: 'pointer' }}
                                            />
                                            <Chip size="small" label="Reagendamento"
                                                onClick={() => toggleStatus('reagendamento_solicitado')}
                                                variant={filtroStatuses.includes('reagendamento_solicitado') ? 'filled' : 'outlined'}
                                                sx={filtroStatuses.includes('reagendamento_solicitado')
                                                    ? { bgcolor: '#fff8e1', color: '#e65100', fontWeight: 'bold', borderColor: '#ffa726', cursor: 'pointer' }
                                                    : { borderColor: '#bdbdbd', cursor: 'pointer' }}
                                            />
                                            <Chip size="small" label="Concluída"
                                                onClick={() => toggleStatus('concluida')}
                                                variant={filtroStatuses.includes('concluida') ? 'filled' : 'outlined'}
                                                sx={filtroStatuses.includes('concluida')
                                                    ? { bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', borderColor: '#66bb6a', cursor: 'pointer' }
                                                    : { borderColor: '#bdbdbd', cursor: 'pointer' }}
                                            />
                                        </Stack>
                                    </Grid>

                                </Grid>
                            </Paper>
                        </Collapse>

                        {/* ── Mensagem de vazio ─────────────────────────────── */}
                        {tarefasCriadas.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">Você ainda não criou nenhuma tarefa.</Typography>
                            </Box>
                        )}
                        {tarefasCriadas.length > 0 && tarefasCriadasFiltradas.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <FilterListIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography color="text.secondary">Nenhuma tarefa encontrada com os filtros aplicados.</Typography>
                                <Button size="small" sx={{ mt: 1 }} onClick={limparFiltros}>Limpar filtros</Button>
                            </Box>
                        )}

                        {/* Em andamento agrupadas por data */}
                        {criadasPorData.map(([dateStr, tarefas], gi) => (
                            <Box key={dateStr} sx={{ mb: 2 }}>
                                {gi > 0 && <Divider sx={{ mb: 2 }} />}
                                <DateGroupHeader dateStr={dateStr} />
                                <Grid container spacing={1.5}>
                                    {tarefas.map(t => (
                                        <Grid item xs={12} sm={6} key={t.id_tarefa}>
                                            <CriadasCard
                                                tarefa={t}
                                                onAprovar={setAprovando}
                                                onEditar={handleEditar}
                                                onDeletar={handleDeletar}
                                                podeEditar={podeEditar}
                                                podeDeletar={podeDeletar}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}

                        {/* Paginação – Em andamento */}
                        {totalPagCriadas > 1 && (
                            <Stack alignItems="center" mt={2} mb={1}>
                                <Pagination
                                    count={totalPagCriadas}
                                    page={pagCriadas}
                                    onChange={(_, v) => { setPagCriadas(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    color="primary"
                                    size="small"
                                />
                                <Typography variant="caption" color="text.secondary" mt={0.5}>
                                    {criaPendentes.length} tarefas · página {pagCriadas} de {totalPagCriadas}
                                </Typography>
                            </Stack>
                        )}

                        {criaConcluidas.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="overline" color="text.secondary" fontWeight="bold">
                                    Concluídas ({criaConcluidas.length})
                                </Typography>
                                <Grid container spacing={1.5} mt={0}>
                                    {criaConclPage.map(t => (
                                        <Grid item xs={12} sm={6} key={t.id_tarefa}>
                                            <CriadasCard
                                                tarefa={t}
                                                onAprovar={setAprovando}
                                                onEditar={handleEditar}
                                                onDeletar={handleDeletar}
                                                podeEditar={podeEditar}
                                                podeDeletar={podeDeletar}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {totalPagConclCriadas > 1 && (
                                    <Stack alignItems="center" mt={2}>
                                        <Pagination
                                            count={totalPagConclCriadas}
                                            page={pagConclCriadas}
                                            onChange={(_, v) => setPagConclCriadas(v)}
                                            color="primary"
                                            size="small"
                                        />
                                        <Typography variant="caption" color="text.secondary" mt={0.5}>
                                            {criaConcluidas.length} concluídas · página {pagConclCriadas} de {totalPagConclCriadas}
                                        </Typography>
                                    </Stack>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Paper>
        </Templete>
    );
}
